import { useParams } from 'react-router-dom';
import { useLeague } from '../../features/leagues/api';
import { useStandings } from '../../features/matchdays/api';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatPoints } from '../../lib/dates';
import { AppLayout } from '../../components/AppLayout';
import { LeagueNav } from './LeagueNav';

/**
 * Classifica (lineeguida §36): tabella semantica navigabile.
 * Le medaglie sono decorative; posizione e variazione sono testo vero.
 */
export function StandingsPage() {
  const { id = '' } = useParams();
  const league = useLeague(id);
  usePageTitle(league.data ? `Classifica — ${league.data.name}` : 'Classifica');
  const standings = useStandings(id);

  const medal = (position: number): string | null =>
    position === 1 ? '🥇' : position === 2 ? '🥈' : position === 3 ? '🥉' : null;

  const deltaText = (delta: number): string => {
    if (delta > 0) return `salito di ${delta} ${delta === 1 ? 'posizione' : 'posizioni'}`;
    if (delta < 0) return `sceso di ${-delta} ${delta === -1 ? 'posizione' : 'posizioni'}`;
    return 'posizione invariata';
  };

  return (
    <AppLayout title={league.data ? `Classifica — ${league.data.name}` : 'Classifica'}>
      <LeagueNav leagueId={id} />

      {standings.isLoading && (
        <p role="status" className="mt-6">Caricamento classifica…</p>
      )}

      {standings.data && standings.data.rows.length === 0 && (
        <p className="mt-6 text-lg">
          La classifica apparirà dopo la pubblicazione della prima giornata.
        </p>
      )}

      {standings.data && standings.data.rows.length > 0 && (
        <>
          <p className="mt-4 text-ink-700 dark:text-ink-200">
            Aggiornata alla giornata {standings.data.matchdayNumber}.
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <caption className="visually-hidden">
                Classifica della lega: posizione, giocatore, punti totali, variazione e giornate giocate
              </caption>
              <thead>
                <tr className="border-b-2 border-ink-300 dark:border-ink-600">
                  <th scope="col" className="py-2 pr-3">Posizione</th>
                  <th scope="col" className="py-2 pr-3">Giocatore</th>
                  <th scope="col" className="py-2 pr-3 text-right">Punti</th>
                  <th scope="col" className="py-2 pr-3">Variazione</th>
                  <th scope="col" className="py-2 text-right">Giornate</th>
                </tr>
              </thead>
              <tbody>
                {standings.data.rows.map((row) => (
                  <tr key={row.memberId} className="border-b border-ink-200 dark:border-ink-800">
                    <td className="py-3 pr-3 text-lg font-bold">
                      {row.position}
                      {medal(row.position) && <span aria-hidden="true"> {medal(row.position)}</span>}
                    </td>
                    <th scope="row" className="py-3 pr-3 font-medium">
                      {row.displayName}
                      {row.isBot && (
                        <span className="ml-2 rounded-full bg-panda-100 px-2 py-0.5 text-xs font-semibold text-panda-900 dark:bg-panda-900 dark:text-panda-100">
                          Bot
                        </span>
                      )}
                    </th>
                    <td className="py-3 pr-3 text-right text-lg font-bold">
                      {formatPoints(row.totalPoints)}
                    </td>
                    <td className="py-3 pr-3">
                      <span aria-hidden="true">
                        {row.positionDelta > 0 && `▲ ${row.positionDelta}`}
                        {row.positionDelta < 0 && `▼ ${-row.positionDelta}`}
                        {row.positionDelta === 0 && '—'}
                      </span>
                      <span className="visually-hidden">{deltaText(row.positionDelta)}</span>
                    </td>
                    <td className="py-3 text-right">{row.playedCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AppLayout>
  );
}
